import { useCallback } from 'react';
import moment from 'moment-timezone';

import useRequestJSON, { RequestRunner } from './hooks/useRequestJSON';
import {
  PostJSON,
  PaginatedParams,
  BaseResponse,
  ErrorResponse
} from './request';

import { GetTagsByValue } from './tag';
import { GetPhoneFromInput } from './format';

import { AppKey } from './apps';
import { PatientGroupFilters } from './group';
import { ITask } from './tasks';

export enum Gender {
  Male = 'Male',
  Female = 'Female',
  Unknown = 'Unknown',
  Other = 'Other',
  Nonbinary = 'Nonbinary'
}

export interface IPatient {
  id: number;
  firstName: string;
  middleInitial?: string;
  lastName: string;
  phone: string;
  caregiverPhone?: string;
  phoneIsCaregiver?: boolean;
  additionalPhones?: string;
  email?: string;
  dob?: string;
  gender?: Gender;
  note?: string;
  tags?: string;
  mrn?: string;
  csn?: string;
  facility?: string;
  hash?: string;
  receivedIntro?: boolean;
  ccmEligible?: boolean;
  addressedAt?: Date;
  historicAddressedAt?: Date;
  historicAddressedById?: number;
  snoozedUntil?: Date;
  snoozedAt?: Date;
  snoozedById?: number;
  isSuspended?: boolean;
  suspendedAt?: Date;
  isLandline?: boolean;
  twilioFailureCount?: number;
  isArchived?: boolean;
  archivedAt?: Date;
  batchId?: number;
  createdById: number;
  phoneInfoId?: number;
  enrolledAt?: Date;
  createdAt: Date;
  updatedAt?: Date;
  deletedAt?: Date;
  // Triage use only
  _justResolved?: boolean;
  _justSnoozed?: boolean;
  // Only in comprehensive search
  interventions?: string; // comma separated app keys
  contacts?: string; // comma separated IDs
  // Queue
  tasks?: ITask[]; // Task Objects
}

export type PatientTabIdType =
  AppKey | 'information' | 'events' | 'time-tracked';

// Requests & Hooks

interface GetPatientsParams extends PaginatedParams {
  name?: string;
  phone?: string;
  isSuspended?: boolean;
  includeArchived?: boolean;
  filters?: PatientGroupFilters;
}

export interface GetPatientsResponse extends BaseResponse {
  patients: IPatient[];
  total: number;
  isLoading?: boolean;
}

interface GetOnePatientResponse {
  patient?: IPatient;
}

const GetOnePatientTransformer = (
  response: GetPatientsResponse
): GetOnePatientResponse => {
  return { patient: response.patients[0] };
};

interface GetOneResult extends GetOnePatientResponse {
  run: RequestRunner;
}

export const useGetOnePatient = (id: number): GetOneResult => {
  const url = '/patient/data/';
  const request = useCallback(
    () =>
      PostJSON({
        url: url,
        params: { id }
      }),
    [id]
  );
  const { data, run } = useRequestJSON<
    GetPatientsResponse,
    GetOnePatientResponse
  >({
    request,
    transformFn: GetOnePatientTransformer,
    enabled: id != null
  });
  return {
    patient: data?.patient,
    run
  };
};

export const getOnePatient = async (
  id: number,
  includeArchived = false
): Promise<GetOnePatientResponse> => {
  const url = '/patient/data/';
  const response = await PostJSON<GetPatientsResponse>({
    url: url,
    params: { id, includeArchived }
  });
  return GetOnePatientTransformer(response as GetPatientsResponse);
};

export const transformPatients = (
  response: GetPatientsResponse
): Omit<GetPatientsResponse, 'status'> => {
  return {
    patients: response.patients,
    total: response.total
  };
};

export const useGetPatients = (
  params: GetPatientsParams,
  isEnabled?: boolean
): Omit<GetPatientsResponse, 'status'> => {
  const url = '/patient/data/';
  const request = useCallback(
    () =>
      PostJSON({
        url: url,
        params: params
      }),
    [
      params.name,
      params.phone,
      params.isSuspended,
      params.includeArchived,
      params.page,
      params.limit,
      params.filters
    ]
  );
  const { isLoading, data } = useRequestJSON<
    GetPatientsResponse,
    Omit<GetPatientsResponse, 'status'>
  >({
    request,
    enabled: isEnabled
  });
  return {
    isLoading,
    patients: data?.patients || [],
    total: data?.total || 0
  };
};

interface CheckDuplicateParams {
  firstName?: string;
  lastName?: string;
  phone?: string;
  mrn?: string;
}

interface CheckDuplicateResponse {
  status: string;
  patients: IPatient[];
}

export const useCheckDuplicatePatients = (
  params: CheckDuplicateParams,
  enabled?: boolean
): Omit<CheckDuplicateResponse, 'status'> => {
  const url = '/patient/data_duplicate/';
  const request = useCallback(
    () =>
      PostJSON({
        url: url,
        params: params
      }),
    [params.firstName, params.lastName, params.phone]
  );
  const { data } = useRequestJSON<
    CheckDuplicateResponse,
    Omit<CheckDuplicateResponse, 'status'>
  >({
    request,
    enabled: enabled !== false
  });
  return {
    patients: data?.patients || []
  };
};

export interface PatientSearchParams extends PaginatedParams {
  id?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  dob?: string;
  mrn?: string;
  includeArchived?: boolean;
}

interface PatientSearchResults {
  total: number;
  patients: IPatient[];
  isLoading: boolean;
}

export const usePatientSearch = (
  params: PatientSearchParams
): PatientSearchResults => {
  const url = '/patient/data_search/';
  if (params.id) {
    params = {
      id: params.id,
      includeArchived: params.includeArchived
    };
  }
  /**
   * Strip phone param of all formatting
   * i.e (314) 000-3333 -> 3140003333
   */
  if (params.phone && params.phone.length) {
    params.phone = params.phone.replace(/\D+/g, '');
  }
  const request = useCallback(
    () =>
      PostJSON({
        url: url,
        params: params
      }),
    [
      params.page,
      params.limit,
      params.id,
      params.name,
      params.firstName,
      params.lastName,
      params.phone,
      params.mrn,
      params.dob,
      params.includeArchived
    ]
  );
  const { data, isLoading } = useRequestJSON<
    GetPatientsResponse,
    Omit<GetPatientsResponse, 'status'>
  >({
    request,
    transformFn: transformPatients
  });
  return {
    total: data?.total || 0,
    patients: data?.patients || [],
    isLoading
  };
};

// Requests

export interface PatientFormData {
  id?: number;
  firstName?: string;
  middleInitial?: string;
  lastName?: string;
  phone?: string;
  caregiverPhone?: string;
  mrn?: string;
  email?: string;
  dob?: string;
  gender?: string;
  note?: string;
  descriptionHtml?: string;
  tags?: string;
  csn?: string;
  ccmEligible?: boolean;
}

export interface SavePatientResponse extends BaseResponse {
  patient?: IPatient;
}

export interface SuspendPatientResponse extends BaseResponse {
  patient?: IPatient;
  tasks?: ITask[];
  pausedApps?: string[];
}

export const SavePatient = async (data: PatientFormData): Promise<IPatient> => {
  /**
   * When data values are not present,
   * must use undefined instead of null. The backend
   * currently only checks for undefined, and thus
   * null values end up setting patient.dob
   * and patient.tags to null.
   */
  if (data.caregiverPhone === '') {
    data.caregiverPhone = null;
  } else if (!data.caregiverPhone) {
    data.caregiverPhone = undefined;
  }
  if (data.dob === '') {
    data.dob = null;
  } else if (!data.dob) {
    data.dob = undefined;
  }
  const tags = await GetTagsByValue(data.tags);
  if (tags.length > 0) {
    data.tags = tags.map((tag) => tag.id).join(',');
  } else if (data.tags === '') {
    data.tags = null;
  } else if (!data.tags) {
    data.tags = undefined;
  }
  data.phone = GetPhoneFromInput(data.phone);
  if (data.gender?.trim() === '') {
    data.gender = null;
  }
  if (data.descriptionHtml != null) {
    data.note = undefined;
  }
  const url = data.id != null ? '/patient/edit/' : '/patient/create/';
  const response = await PostJSON<SavePatientResponse, PatientFormData>({
    url: url,
    params: data
  });
  if (response.status !== 'OK') {
    throw new Error(response.message);
  }
  return (response as SavePatientResponse).patient;
};

interface ReprescribePatientParams {
  app: AppKey;
  patientId: IPatient['id'];
}

export const ReprescribePatient = (
  params: ReprescribePatientParams
): Promise<BaseResponse | ErrorResponse> => {
  return PostJSON<BaseResponse>({
    url: '/patient/represcribe/',
    params: params
  });
};

// Utilities

export interface PatientLike {
  firstName: string;
  middleInitial?: string;
  lastName?: string;
}

export const GetFullName = (patient: PatientLike): string => {
  let name = patient.firstName;
  if (patient.middleInitial && patient.middleInitial.trim().length > 0) {
    name += ' ' + patient.middleInitial[0] + '.';
  }
  name += ' ' + patient.lastName;
  return name;
};

export const IsPatientSnoozed = (patient: IPatient): boolean => {
  return (
    patient.snoozedUntil != null && moment(patient.snoozedUntil) > moment()
  );
};

export const IsPatientArchived = (patient: IPatient): boolean => {
  return patient.isArchived;
};

export const IsPatientPaused = (patient: IPatient): boolean => {
  return patient.isSuspended;
};

export const GetPatientPhone = (
  phone: string,
  phoneIsCaregiver: boolean
): string => {
  if (phoneIsCaregiver) {
    return '';
  }
  return phone;
};

export const GetCaregiverPhone = (
  phone: string,
  phoneIsCaregiver: boolean,
  caregiverPhone?: string
): string => {
  if (phoneIsCaregiver) {
    if (caregiverPhone?.length) {
      return caregiverPhone;
    }
    return phone;
  }
  return '';
};
